import { z } from 'zod';
import { AuditedFields, IsoDateTime, QueryBoolean, Uuid, Version } from './common.js';
/**
 * Happy hours (ADR-019, D-H23, module `bar`) : règles de prix horaires d'un
 * point de vente. La plage horaire s'entend dans le **fuseau de
 * l'établissement** ; `endTime <= startTime` signifie que la plage franchit
 * minuit. Le prix réduit est figé à l'ajout de la ligne (R-BAR-03) : une règle
 * modifiée ensuite ne réécrit jamais une commande.
 */
export const HappyHourDiscountType = z.enum(['PERCENT', 'AMOUNT']);
/** `HH:MM` (24 h). */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM');
/** Jour ISO : 1 = lundi … 7 = dimanche. */
export const IsoWeekday = z.number().int().min(1).max(7);
export const HappyHour = z
    .object({
    id: Uuid,
    outletId: Uuid,
    name: z.string(),
    daysOfWeek: z.array(IsoWeekday),
    startTime: ClockTime,
    endTime: ClockTime,
    discountType: HappyHourDiscountType,
    discountValue: z.number(),
    /** Catégories concernées ; vide = toute la carte du point de vente. */
    categoryIds: z.array(Uuid),
    isActive: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
const discountRefinement = (input, ctx) => {
    if (input.discountType === 'PERCENT' && input.discountValue !== undefined) {
        if (input.discountValue > 100) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['discountValue'],
                message: 'Une remise ne dépasse pas 100 %',
            });
        }
    }
};
export const CreateHappyHourRequest = z
    .object({
    name: z.string().trim().min(2).max(80),
    daysOfWeek: z.array(IsoWeekday).min(1).max(7).default([1, 2, 3, 4, 5, 6, 7]),
    startTime: ClockTime,
    endTime: ClockTime,
    discountType: HappyHourDiscountType,
    discountValue: z.number().positive(),
    categoryIds: z.array(Uuid).max(50).default([]),
})
    .superRefine(discountRefinement);
export const UpdateHappyHourRequest = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    daysOfWeek: z.array(IsoWeekday).min(1).max(7).optional(),
    startTime: ClockTime.optional(),
    endTime: ClockTime.optional(),
    discountType: HappyHourDiscountType.optional(),
    discountValue: z.number().positive().optional(),
    categoryIds: z.array(Uuid).max(50).optional(),
    isActive: z.boolean().optional(),
    version: Version,
})
    .superRefine(discountRefinement);
export const ListHappyHoursQuery = z.object({ includeInactive: QueryBoolean.optional() });
/** Règles actives à l'instant, avec l'heure et le fuseau du serveur (affichage caisse). */
export const CurrentHappyHours = z.object({
    now: IsoDateTime,
    timezone: z.string(),
    /** `HH:MM` local de l'établissement. */
    localTime: ClockTime,
    rules: z.array(HappyHour),
});
//# sourceMappingURL=happy-hours.js.map